import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import Breadcrumb from "../components/Breadcrumb";
import Images from "../assets/images/sign-in.jpg";
import googleimg from "../assets/images/google.png";
import facebookimg from "../assets/images/facebook.png";
import {
  SendOTP,
  UpdateUserProfile1st,
  VerifyOTP,
  GoogleSignIn,
  UpdateUserProfile,
} from "../API/LoginAPI";
import { toast } from "sonner";
import FormLoader from "../components/FormLoader";
import { GoogleLogin, GoogleOAuthProvider } from "@react-oauth/google";
import { jwtDecode } from "jwt-decode";

const Signin = () => {
  const [showOtp, setShowOtp] = useState(false);
  const [showNamePopup, setShowNamePopup] = useState(false);
  const [showMobilePopup, setShowMobilePopup] = useState(false);
  const [otp, setOtp] = useState(["", "", "", ""]);
  const [ccode, setCcode] = useState("+91");
  const [mobile, setMobile] = useState("");
  const [resendTimer, setResendTimer] = useState(0);
  const [FormLoading, setFormLoading] = useState(false);
  const [userData, setUserData] = useState(null);
  const [nameInput, setNameInput] = useState("");
  const [mobileInput, setMobileInput] = useState("");

  const navigate = useNavigate();

  const countries = [
    { code: "+91", name: "India", flag: "https://flagcdn.com/w20/in.png" },
    { code: "+1", name: "USA", flag: "https://flagcdn.com/w20/us.png" },
  ];

  // --- FORM LOGIN WITH OTP ---
  const formik = useFormik({
    initialValues: { phone: "" },
    validationSchema: Yup.object({
      phone: Yup.string()
        .matches(/^[0-9]{10}$/, "Enter valid 10 digit number")
        .required("Phone is required"),
    }),
    onSubmit: async (values) => {
      setFormLoading(true);
      try {
        const payload = { CCODE: ccode, EOP: values.phone };
        const data = await SendOTP(payload);
        if (data.status === true) {
          toast.success(data.message);
          setMobile(values.phone);
          setShowOtp(true);
          setResendTimer(60);
        } else {
          toast.error("Failed to send OTP");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setFormLoading(false);
      }
    },
  });

  const handleOtpChange = (val, idx) => {
    if (/^[0-9]?$/.test(val)) {
      const newOtp = [...otp];
      newOtp[idx] = val;
      setOtp(newOtp);
      if (val && idx < 3) {
        document.getElementById(`otp-${idx + 1}`).focus();
      }
    }
  };

  const handleVerifyOtp = async () => {
    const enteredOtp = otp.join("");
    if (enteredOtp.length !== 4) {
      toast.error("Enter valid 4 digit OTP");
      return;
    }

    setFormLoading(true);
    try {
      const payload = { CCODE: ccode, EOP: mobile, OTP: enteredOtp };
      const data = await VerifyOTP(payload);
      setUserData(data.responseData);

      if (data.status === true) {
        sessionStorage.setItem(
          "AuthToken",
          JSON.stringify(data.responseData.token || "")
        );

        if (!data.responseData.Name || data.responseData.Name.trim() === "") {
          setShowOtp(false);
          setShowNamePopup(true);
        } else if (
          !data.responseData.Phone ||
          data.responseData.Phone.trim() === ""
        ) {
          setShowOtp(false);
          setShowMobilePopup(true);
        } else {
          // Check for redirect URL
          const redirectUrl = sessionStorage.getItem("redirectAfterLogin") || "/";
          sessionStorage.removeItem("redirectAfterLogin"); // Clear redirect URL
          navigate(redirectUrl);
          // window.location.reload();
        }
      } else {
        toast.error("Invalid OTP");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleResendOtp = async () => {
    try {
      const payload = { CCODE: ccode, EOP: mobile };
      const data = await SendOTP(payload);
      if (data?.status === true) {
        setOtp(["", "", "", ""]);
        toast.success("OTP resent");
        setResendTimer(60);
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleNameSubmit = async () => {
    if (!nameInput.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setFormLoading(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("AuthToken"));
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }
      const payload = { ...userData, Name: nameInput };
      const data = await UpdateUserProfile1st(payload, token);

      if (data.status === true) {
        setShowNamePopup(false);
        setNameInput("");
        if (!userData?.Phone) {
          setShowMobilePopup(true);
        } else {
          // Check for redirect URL
          const redirectUrl = sessionStorage.getItem("redirectAfterLogin") || "/";
          sessionStorage.removeItem("redirectAfterLogin"); // Clear redirect URL
          navigate(redirectUrl);
          // window.location.reload();
        }
      } else {
        toast.error("Failed to update profile");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  const handleMobileSubmit = async () => {
    if (!mobileInput.trim()) {
      toast.error("Please enter your mobile number");
      return;
    }
    setFormLoading(true);
    try {
      const token = JSON.parse(sessionStorage.getItem("AuthToken"));
      if (!token) {
        toast.error("Unauthorized. Please login again.");
        return;
      }
      const payload = { ...userData, CCODE: ccode, Phone: mobileInput };
      const data = await UpdateUserProfile1st(payload, token);

      if (data.status === true) {
        setShowMobilePopup(false);
        setMobileInput("");
        // Check for redirect URL
        const redirectUrl = sessionStorage.getItem("redirectAfterLogin") || "/";
        sessionStorage.removeItem("redirectAfterLogin"); // Clear redirect URL
        navigate(redirectUrl);
        // window.location.reload();
      } else {
        toast.error("Failed to update profile");
      }
    } catch (err) {
      console.error(err);
    } finally {
      setFormLoading(false);
    }
  };

  // --- GOOGLE SIGN IN HANDLER ---
  const handleGoogleSuccess = async (credentialResponse) => {
    try {
      const decoded = jwtDecode(credentialResponse.credential);

      const payload = {
        Name: decoded.name,
        SocialId: decoded.sub,
        Email: decoded.email,
      };

      setFormLoading(true);
      const data = await GoogleSignIn(payload);

      if (data.status === true) {
        setUserData(data.responseData);
        sessionStorage.setItem(
          "AuthToken",
          JSON.stringify(data.responseData.token || "")
        );

        if (!data.responseData.Name) {
          setShowNamePopup(true);
        } else if (!data.responseData.Phone) {
          setShowMobilePopup(true);
        } else {  
          const redirectUrl = sessionStorage.getItem("redirectAfterLogin") || "/";
          sessionStorage.removeItem("redirectAfterLogin"); // Clear redirect URL
          navigate(redirectUrl);
          // window.location.reload();
        }
      } else {
        toast.error("Google Sign In failed");
      }
    } catch (err) {
      console.error(err);
      toast.error("Google Sign In error");
    } finally {
      setFormLoading(false);
    }
  };

  // --- TIMER EFFECT ---
  useEffect(() => {
    let interval = null;
    if (resendTimer > 0) {
      interval = setInterval(() => {
        setResendTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [resendTimer]);

  // Close popups
  const closeOtpPopup = () => {
    setShowOtp(false);
    setOtp(["", "", "", ""]);
  };
  const closeNamePopup = () => {
    setShowNamePopup(false);
    setNameInput("");
  };
  const closeMobilePopup = () => {
    setShowMobilePopup(false);
    setMobileInput("");
  };

  return (
    <>
      <GoogleOAuthProvider clientId="973202346452-b1bagua4spsjscubs8egnbheo29ddrfi.apps.googleusercontent.com">
        <Breadcrumb title="Sign In" path="Sign In" />
        {FormLoading && <FormLoader loading={FormLoading} />}

        {/* --- FORM --- */}
        <div className="container mx-auto px-4 py-20">
          <div className="grid grid-cols-1 md:grid-cols-2 w-full rounded-lg overflow-hidden">
            <div className="hidden md:block px-20">
              <img
                src={Images}
                alt="food"
                className="w-full h-[32rem] object-cover"
              />
            </div>
            <div className="flex flex-col justify-center md:p-10 px-5 md:px-20">
              <h2 className="md:text-4xl text-2xl text-darktext font-bold mb-2">
                Account
              </h2>
              <p className="text-contentcolor mb-6">Log in or sign up</p>
              <form onSubmit={formik.handleSubmit}>
                <div className="flex items-center rounded-md bg-gray-100 px-3 py-5 mb-2">
                  <div className="relative mr-2">
                    <select
                      value={ccode}
                      onChange={(e) => setCcode(e.target.value)}
                      className="cursor-pointer  bg-transparent outline-none text-black pl-10 pr-2"
                    >
                      {countries.map((c) => (
                        <option key={c.code} value={c.code}>
                          {c.code}
                        </option>
                      ))}
                    </select>
                    {countries.map(
                      (c) =>
                        c.code === ccode && (
                          <img
                            key={c.code}
                            src={c.flag}
                            alt={c.name}
                            className="w-7 h-5 absolute left-0 top-1/2 -translate-y-1/2 ml-1"
                          />
                        )
                    )}
                  </div>
                  <input
                    type="text"
                    name="phone"
                    placeholder="Enter your number"
                    value={formik.values.phone}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    className="w-full bg-transparent outline-none text-black"
                  />
                </div>
                {formik.touched.phone && formik.errors.phone && (
                  <p className="text-red-500 text-sm mb-4">
                    {formik.errors.phone}
                  </p>
                )}
                <button
                  type="submit"
                  className="w-full bg-theme2 hover:bg-black text-white py-4 rounded-md font-semibold"
                >
                  Send OTP
                </button>
              </form>

              <div className="flex items-center my-6">
                <div className="flex-1 h-px bg-gray-300"></div>
                <span className="px-2 text-contentcolor text-sm">or</span>
                <div className="flex-1 h-px bg-gray-300"></div>
              </div>

              <div className="flex gap-6 justify-center flex-col md:flex-row">
                <GoogleLogin
                  onSuccess={handleGoogleSuccess}
                  onError={() => toast.error("Google Login Failed")}
                />
                {/* <button className="flex items-center justify-center hover:bg-theme2 hover:text-white transition-all duration-200 ease-in-out gap-2 bg-gray-100 px-7 py-4 text-lg rounded-md font-medium">
                  <img src={facebookimg} alt="facebook" className="w-8 h-8" />
                  Facebook
                </button> */}
              </div>
            </div>
          </div>
        </div>

        {/* --- OTP Modal --- */}
        {showOtp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8 text-center relative">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-black"
                onClick={closeOtpPopup}
              >
                ✕
              </button>
              <h2 className="md:text-2xl text-xl font-semibold mb-2">
                Verify Your OTP
              </h2>
              <p className="text-contentcolor mb-6">
                Enter the 4-digit OTP sent to your number.
              </p>
              <div className="flex justify-center gap-3 mb-6">
                {otp.map((val, i) => (
                  <input
                    key={i}
                    id={`otp-${i}`}
                    type="text"
                    maxLength="1"
                    value={val}
                    onChange={(e) => handleOtpChange(e.target.value, i)}
                    className="w-12 h-12 border border-gray-600 rounded-md text-center text-lg focus:outline-none focus:border-theme2"
                  />
                ))}
              </div>
              <button
                onClick={handleVerifyOtp}
                className="w-full bg-theme2 hover:bg-black text-white py-4 rounded-md font-semibold mb-4"
              >
                Verify OTP
              </button>
              <p className="text-contentcolor">
                Didn’t receive the code?{" "}
                <button
                  onClick={handleResendOtp}
                  className={`font-semibold ${resendTimer > 0
                    ? "text-contentcolor cursor-not-allowed"
                    : "text-theme2"
                    }`}
                  disabled={resendTimer > 0}
                >
                  {resendTimer > 0 ? `Resend in ${resendTimer}s` : "Resend OTP"}
                </button>
              </p>
            </div>
          </div>
        )}

        {/* --- Name Modal --- */}
        {showNamePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8 text-center relative">
              <h2 className="md:text-2xl text-xl font-semibold mb-2">
                Add Your Name
              </h2>
              <p className="text-contentcolor mb-6">
                Please enter your name to complete your profile.
              </p>
              <input
                type="text"
                placeholder="Enter your name"
                value={nameInput}
                onChange={(e) => setNameInput(e.target.value)}
                className="w-full border border-gray-400 rounded-md px-3 py-3 mb-4 focus:outline-none focus:border-theme2"
              />
              <button
                onClick={handleNameSubmit}
                className="w-full bg-theme2 hover:bg-black text-white py-4 rounded-md font-semibold"
              >
                Submit
              </button>
            </div>
          </div>
        )}

        {/* --- Mobile Modal --- */}
        {showMobilePopup && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-8 text-center relative">
              <h2 className="md:text-2xl text-xl font-semibold mb-2">
                Add Your Mobile Number
              </h2>
              <p className="text-contentcolor mb-6">
                Please enter your mobile number to complete your profile.
              </p>
              <div className="flex items-center rounded-md border px-2 py-2 mb-4">
                <select
                  value={ccode}
                  onChange={(e) => setCcode(e.target.value)}
                  className="cursor-pointer outline-none text-black mr-2"
                >
                  {countries.map((c) => (
                    <option key={c.code} value={c.code}>
                      {c.code}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Enter mobile number"
                  value={mobileInput}
                  onChange={(e) => setMobileInput(e.target.value)}
                  className="w-full outline-none text-black"
                />
              </div>
              <button
                onClick={handleMobileSubmit}
                className="w-full bg-theme2 hover:bg-black text-white py-4 rounded-md font-semibold"
              >
                Submit
              </button>
            </div>
          </div>
        )}
      </GoogleOAuthProvider>
    </>
  );
};

export default Signin;